import { difference } from 'lodash-es';

/**
 * Data structure for representing a one-to-many or parent-children relationship by maintaining a forward
 * mapping from parent to children and a reverse mapping from child to parent.
 *
 * @example ```
 * const proxiedDeviceAssociation = new OneToManyAssociation<Device, Device>(
 *     (hostDevice) => hostDevice.id,
 *     (proxiedDevice) => proxiedDevice.id
 * );
 *
 * proxiedDeviceAssociation.set(hostDevice, hostDevice.features.proxiedDevices);
 * const host = proxiedDeviceAssociation.getParent(proxiedDevice.id);
 * ```
 */
export class OneToManyAssociation<TParent, TChild> {
	private readonly parentToChildrenMap = new Map<string, TChild[]>();
	private readonly childToParentMap = new Map<string, TParent>();

	constructor(
		private readonly getParentKey: (parent: TParent) => string,
		private readonly getChildKey: (child: TChild) => string
	) {}

	/**
	 * Associates children with a parent. To clear all children for the parent, pass in an empty array.
	 */
	public set(parent: TParent, children: TChild[]): void {
		const parentKey = this.getParentKey(parent);
		const existingChildKeys = (this.parentToChildrenMap.get(parentKey) ?? []).map((child) =>
			this.getChildKey(child)
		);
		const childKeys = children.map((child) => this.getChildKey(child));

		// Remove children that are no longer associated with the parent
		for (const removedKey of difference(existingChildKeys, childKeys)) {
			const foundParent = this.childToParentMap.get(removedKey);
			if (foundParent && this.getParentKey(foundParent) === parentKey) {
				this.childToParentMap.delete(removedKey);
			}
		}

		// Add/update associated children
		this.parentToChildrenMap.delete(parentKey);
		if (children.length > 0) {
			this.parentToChildrenMap.set(parentKey, children);
		}

		for (const childKey of childKeys) {
			this.childToParentMap.set(childKey, parent);
		}
	}

	/**
	 * Given a key, will determine if it belongs to a parent or child, remove the associated entity, and update
	 * relationships accordingly.
	 * - if key belongs to parent, the parent and all of its children will be removed
	 * - if key belongs to a child, the child to parent association will be removed
	 */
	public removeChildOrParent(parentOrChildKey: string): void {
		// If parent, remove parent and all child mappings
		const children = this.parentToChildrenMap.get(parentOrChildKey);
		if (children) {
			this.parentToChildrenMap.delete(parentOrChildKey);
			for (const child of children) {
				this.childToParentMap.delete(this.getChildKey(child));
			}

			return;
		}

		// If child, remove parent and child mappings
		const parent = this.childToParentMap.get(parentOrChildKey);
		if (parent) {
			this.childToParentMap.delete(parentOrChildKey);
			this.parentToChildrenMap.delete(this.getParentKey(parent));
		}
	}

	/**
	 * Get the parent for a given child key, if it exists
	 */
	public getParent(childKey: string): TParent | undefined {
		return this.childToParentMap.get(childKey);
	}

	/**
	 * Get all children for a given parent key, if they exist
	 */
	public getChildren(parentKey: string): TChild[] | undefined {
		return this.parentToChildrenMap.get(parentKey);
	}
}
